import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { CHART_COLORS } from '../../lib/chart-theme';

interface LineSeries {
    name: string;
    data: (number | null)[];
    color?: string;
    lineStyle?: 'solid' | 'dashed';
}

interface ReportLineChartProps {
    title: string;
    categories: string[];
    series: LineSeries[];
    loading?: boolean;
    height?: number;
    valueFormatter?: (value: number) => string;
    onPointClick?: (categoryIndex: number, category: string) => void;
}

export function ReportLineChart({ title, categories, series, loading, height = 400, valueFormatter, onPointClick }: ReportLineChartProps) {
    const fmt = valueFormatter ?? ((v: number) => v.toLocaleString('fr-FR'));

    const option: EChartsOption = {
        tooltip: {
            trigger: 'axis',
            valueFormatter: (value) => fmt(Number(value)),
        },
        legend: { bottom: 0, type: 'scroll' },
        grid: { left: '8%', right: '4%', bottom: '12%', top: '8%', containLabel: true },
        xAxis: {
            type: 'category',
            data: categories,
            axisLabel: { rotate: categories.length > 6 ? 45 : 0, fontSize: 11 },
        },
        yAxis: {
            type: 'value',
            axisLabel: { formatter: (v: number) => fmt(v) },
        },
        series: series.map((s, i) => ({
            name: s.name,
            type: 'line' as const,
            data: s.data,
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            itemStyle: { color: s.color ?? CHART_COLORS[i % CHART_COLORS.length] },
            lineStyle: { type: s.lineStyle ?? 'solid' },
            connectNulls: true,
        })),
    };

    const onEvents = onPointClick
        ? {
              click: (params: any) => {
                  onPointClick(params.dataIndex, categories[params.dataIndex]);
              },
          }
        : undefined;

    return (
        <div>
            <h4 className="text-sm font-medium text-gray-700 text-center mb-1">{title}</h4>
            <ReactECharts
                option={option}
                style={{ height: `${height}px` }}
                showLoading={loading}
                onEvents={onEvents}
            />
        </div>
    );
}
